import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import {
	DragDropContext,
	Draggable,
	type DraggableChildrenFn,
	type DragStart,
	Droppable,
	type DropResult,
} from "react-beautiful-dnd";
import ReactDOM from "react-dom";
import { commitMutation, ConnectionHandler, graphql } from "react-relay";
import { type NavLinkProps, useHistory } from "react-router-dom";

import { faStar } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import {
	faBullseyeArrow,
	faCalendarWeek,
	faCheckSquare,
	faEllipsisV,
	faHeartRate,
	faSearch,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faUserPlus } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import type { LocationDescriptor } from "history";
import { compact, noop } from "lodash";

import AppContext from "~/common/AppContext";
import { DismissibleElementsContext } from "~/common/DismissibleElements";
import {
	type FilterFunction,
	GenericInvite,
	type Invitees,
	OneOnOneInvite,
	type OneOnOneInvitees,
	type OneOnOneToggleStreams,
} from "~/invite";
import { ScrollContext } from "~/layout/ScrollContext";
import { sawBeacon } from "~/lib/beacons";
import { useMedia } from "~/lib/hooks";
import { commitMutationPromise, isNotOther, SingletonRetainer, useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { getFirstUserFacingError, isNative } from "~/lib/utils";
import { environment } from "~/models";
import HomePanelGroup from "~/nav/panels/Home/Sections/HomePanelGroup";
import TeamsSection from "~/nav/panels/Home/Sections/TeamsGroup";
import { GlobalSearchContext } from "~/search/GlobalSearchContext";
import CreateGroupButton from "~/stream/CreateGroupButton";
import { Tooltip, useModals } from "~/ui";
import IconButton from "~/ui/legacy/IconButton";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import Menu from "~/ui/menu";
import toast, { ToastActionButton } from "~/ui/toast";
import DismissibleTooltip from "~/ui/Tooltip/DismissibleTooltip";

import { PrimaryNavLink, PrimaryNavLinkUnreadDot } from "../shared";
import MeetingsGroup from "./Sections/MeetingsGroup";

import type { HomeBulkMoveToOneOnOneGroupMutation } from "~/__generated__/HomeBulkMoveToOneOnOneGroupMutation.graphql";
import type { HomePanelGroupsMenu_viewer$key } from "~/__generated__/HomePanelGroupsMenu_viewer.graphql";
import type { HomePanelMoveGroupMutation as HomePanelMoveGroupMutationType } from "~/__generated__/HomePanelMoveGroupMutation.graphql";
import type { HomePanelMoveItemMutation as HomePanelMoveItemMutationType } from "~/__generated__/HomePanelMoveItemMutation.graphql";
import HomeQueryConcreteQuery, { type HomeQuery as QueryType } from "~/__generated__/HomeQuery.graphql";
import type { OneOnOneLink_user$key as UserFragment } from "~/__generated__/OneOnOneLink_user.graphql";
import type { StreamLink_stream$key as StreamFragment } from "~/__generated__/StreamLink_stream.graphql";

import navPanelStyles from "~/nav/panels/shared/NavPanel.module.css";
import panelSectionStyles from "~/nav/panels/shared/PanelSection.module.css";
import panelStyles from "~/ui/CollapsiblePanel/CollapsiblePanel.module.css";

export type UserData = UserFragment & { __typename: string; id: string; isActive: boolean };
export type StreamData = StreamFragment & {
	__typename: string;
	id: string;
	archived: boolean | null;
	starred: boolean;
	otherUser?: UserData | null;
};

/**
 * A type guard because _.compact is broken in TS 3.7.2 for some reason
 * @todo: Get rid of this after bumping TypeScript, use _.compact instead
 */
function isNotNull<T>(item: T | undefined | null): item is NonNullable<T> {
	return item != null;
}

interface HomePanelContextValue {
	viewer: HomePanelGroupsMenu_viewer$key | null;
	currentlyDragging: { __typename: string; type?: string } | null;
	teams: React.ComponentProps<typeof TeamsSection>["items"];
}
export const HomePanelContext = React.createContext<HomePanelContextValue>({
	viewer: null,
	currentlyDragging: null,
	teams: [],
});

const moveItemMutation = graphql`
	mutation HomePanelMoveItemMutation($objectId: ID!, $groupId: ID!, $position: Int!) @raw_response_type {
		addOrMoveToGroup(objectId: $objectId, groupId: $groupId, position: $position) {
			viewer {
				homePanelGroups {
					id
					...HomePanelGroup_group
					items {
						edges {
							node {
								... on Node {
									id
								}
								... on NoteStream {
									...StreamLink_stream
								}
								... on Tag {
									...TagLink_tag
								}
							}
						}
					}
				}
			}
			movedObject {
				...HomePanelGroupsMenu_object
			}
		}
	}
`;

const moveGroupMutation = graphql`
	mutation HomePanelMoveGroupMutation($groupId: ID!, $position: Int!) @raw_response_type {
		moveGroup(groupId: $groupId, position: $position) {
			viewer {
				homePanelGroups {
					id
					order
				}
			}
		}
	}
`;

// Defined here so it can be reused as Droppable's renderClone prop - see:
// https://github.com/atlassian/react-beautiful-dnd/blob/master/docs/guides/reparenting.md#cloning-api
function getDraggableInnerRenderer(
	groups: NonNullable<NonNullable<QueryType["response"]["viewer"]["homePanelGroups"]>[number]>[],
	viewer: QueryType["response"]["viewer"],
	oneOnOnePlusButtonClick: () => void,
): DraggableChildrenFn {
	return function DraggableGroupClone(provided, snapshot, rubric) {
		const group = groups[rubric.source.index];
		return (
			<HomePanelGroup
				group={group}
				isDragging={snapshot.isDragging}
				ref={provided.innerRef}
				viewer={viewer}
				oneOnOnePlusButtonClick={oneOnOnePlusButtonClick}
				{...provided.draggableProps}
				{...provided.dragHandleProps}
			/>
		);
	};
}

export const HOME_PANEL_OVER_TEN_KEY = "HomePanel__overTenHomePanelItems";

const homeQueryRetainer = new SingletonRetainer();
enum InviteModalSource {
	PLUS_BUTTON = "home_panel__one_on_ones_plus",
	MANAGER_TEAMMATES_BUTTON = "home_panel__one_on_ones_manager_teammates",
	ADD_MORE_BUTTON = "home_panel__one_on_ones_add_more",
}

function HomePanelDataRequiredRenderer({
	headerActionsContainerRef,
	objectiveIconRef,
}: {
	headerActionsContainerRef: React.RefObject<HTMLElement>;
	objectiveIconRef: React.RefObject<HTMLElement>;
}) {
	const [fetchKey, setFetchKey] = React.useState(0);

	const queryVariables = {};

	const props = useLazyLoadQuery<QueryType>(
		graphql`
			query HomeQuery {
				viewer {
					id
					unreadBeacons
					...BillingIdentityFeaturesPaywall_viewer
					...HomePanelGroupsMenu_viewer

					user {
						id
						email
						isGuest

						internalAttributes {
							isManager
							isAccountAdmin
						}
					}

					account {
						allowInviting
						guestUsersAllowInvite
						allowExternalInviting
						billingAccount {
							hasAccessToTeams: hasAccessToFeature(feature: teams)
						}
					}

					myTeams {
						id
						archived
						...TeamLink_info
					}

					homePanelGroups {
						...HomePanelGroup_group
						id
						__typename
						type
						name
						emojiName
						order
						items {
							edges {
								node {
									__typename
									... on NoteStream {
										# this has to be inside '... on TypeName' for the generated types to
										# use a discriminated union
										...StreamLink_stream
										id
										archived
										starred
										type
										otherUser {
											id
											email
										}
									}
									... on Tag {
										__typename
										id
										label
										color
									}
									... on User {
										id
										email
									}
								}
							}
						}
					}
				}
			}
		`,
		queryVariables,
		{ fetchPolicy: "store-and-network", fetchKey },
	);

	homeQueryRetainer.retain(HomeQueryConcreteQuery, queryVariables);

	const refetch = React.useCallback(() => {
		setFetchKey(n => n + 1);
	}, [setFetchKey]);

	const viewer = props.viewer;

	const history = useHistory();

	useEffect(() => {
		if (viewer.unreadBeacons.includes("Navbar__streamsSection")) {
			sawBeacon("Navbar__streamsSection");
		}
	}, [viewer.unreadBeacons]);

	/**************************
	 ******** ONE ON ONE *******
	 ***************************/
	const [oneOnOneInviteOpen, setOneOnOneInviteOpen] = useState(false);
	const [inviteModalSource, setInviteModalSource] = React.useState<string>(InviteModalSource.PLUS_BUTTON);
	const openOneOnOneInvite = useCallback(() => setOneOnOneInviteOpen(true), []);
	const closeOneOnOneInvite = useCallback(() => setOneOnOneInviteOpen(false), []);
	const openInviteModalPlus = useCallback(() => {
		setInviteModalSource(InviteModalSource.PLUS_BUTTON);
		openOneOnOneInvite();
	}, [openOneOnOneInvite]);

	const openInviteModalAddMore = useCallback(() => {
		setInviteModalSource(InviteModalSource.ADD_MORE_BUTTON);
		openOneOnOneInvite();
	}, [openOneOnOneInvite]);

	const groups = useMemo(() => {
		return compact(viewer.homePanelGroups).sort((a, b) => a.order - b.order);
	}, [viewer.homePanelGroups]);

	const onNewOneOnOneInvitees = useCallback(
		({
			invitees,
			streams,
			inviteSent,
		}: {
			invitees?: OneOnOneInvitees;
			streams?: OneOnOneToggleStreams;
			inviteSent: boolean;
		}) => {
			if (invitees && inviteSent) {
				const message =
					invitees.length > 1
						? `Your invite has been successfully sent to ${invitees.length} users`
						: `Your invite to ${invitees[0].user?.fullName} has been successfully sent`;

				toast.success(message, {
					title: "Fellow invite",
					actions: <ToastActionButton onClick={openInviteModalAddMore}>Add more</ToastActionButton>,
				});
			}

			let beforeRefetchDone: () => void = noop;

			const beforeRefetchTask = new Promise<void>(resolve => {
				beforeRefetchDone = resolve;
			});

			const oneononeGroup = groups.find(g => g.type === "ONE_ON_ONE");

			if (inviteModalSource === InviteModalSource.PLUS_BUTTON && oneononeGroup && streams?.length) {
				// the user intended to add the user into the 1on1 section
				// so we do that here
				commitMutationPromise<HomeBulkMoveToOneOnOneGroupMutation>(environment, {
					mutation: graphql`
						mutation HomeBulkMoveToOneOnOneGroupMutation($items: [ObjectGroupPositionInputTuple!]) {
							bulkAddOrMoveToGroup(items: $items) {
								movedObjects {
									...HomePanelGroupsMenu_object
								}
							}
						}
					`,
					variables: { items: streams.map(s => ({ groupId: oneononeGroup.id, objectId: s.id })) },
				}).finally(beforeRefetchDone);
			} else {
				beforeRefetchDone();
			}

			beforeRefetchTask.then(() => {
				refetch();
			});

			const url = invitees?.[0].streamEdge?.node?.url ?? streams?.[0].url;
			if (url) history.push(url);
		},
		[refetch, history, openInviteModalAddMore, inviteModalSource, groups],
	);

	const modals = useModals();

	const openInviteModal = useCallback(async () => {
		const result = await modals.open<{ invitees?: Invitees; inviteSent: boolean }>(({ close }) => (
			<GenericInvite
				onClose={close}
				// eslint-disable-next-line camelcase
				analytics={{ ux_location: "home_panel__invite_card" }}
			/>
		));

		if (!result) return;
		const { invitees, inviteSent } = result;

		refetch();

		if (invitees && inviteSent) {
			const message =
				invitees.length > 1
					? `Your invite has been successfully sent to ${invitees.length} users`
					: `Your invite to ${invitees[0].user?.fullName} has been successfully sent`;

			toast.success(message, {
				title: "Fellow invite",
				actions: <ToastActionButton onClick={openInviteModalAddMore}>Add more</ToastActionButton>,
			});
		}
	}, [modals, openInviteModalAddMore, refetch]);

	const onDropItem = useCallback(
		({ destination, draggableId, type, source }: DropResult) => {
			setCurrentlyDragging(null);
			if (!destination || (destination.index === source.index && destination.droppableId === source.droppableId))
				return;

			if (type === "ITEM") {
				commitMutation<HomePanelMoveItemMutationType>(environment, {
					mutation: moveItemMutation,
					variables: { objectId: draggableId, groupId: destination.droppableId, position: destination.index },
					optimisticUpdater: store => {
						const srcGroup = store.get(source.droppableId);
						const targetGroup = store.get(destination.droppableId);

						if (!targetGroup) return;

						// Array.from to clone the array so we can mutate it before re-setting it
						const remainingSrcEdges = Array.from(srcGroup?.getLinkedRecord("items")?.getLinkedRecords("edges") ?? []);
						const edgeToMove = remainingSrcEdges.splice(source.index, 1)[0];

						if (!remainingSrcEdges) return;
						srcGroup?.getLinkedRecord("items")?.setLinkedRecords(remainingSrcEdges, "edges");

						const targetEdges = Array.from(targetGroup?.getLinkedRecord("items")?.getLinkedRecords("edges") ?? []);

						const itemToMove = edgeToMove?.getLinkedRecord("node");
						if (!srcGroup || !itemToMove) return;
						if (itemToMove.getLinkedRecord("homePanelGroup")) itemToMove.setLinkedRecord(targetGroup, "homePanelGroup");
						const newEdge = ConnectionHandler.createEdge(store, srcGroup, itemToMove, "HomePanelObjectsEdge");

						targetEdges?.splice(destination.index, 0, newEdge);

						targetGroup.getLinkedRecord("items")?.setLinkedRecords(targetEdges, "edges");
					},
					onCompleted: (response, errors) => {
						if (!response.addOrMoveToGroup)
							if (errors && errors.length > 0) {
								const errorMessage = getFirstUserFacingError(errors) ?? errors[0].message;
								toast.error(errorMessage);
							}

						return;
					},
				});
			} else if (type === "GROUP") {
				const newIndex = destination.index;
				const oldIndex = source.index;

				const optimisticGroups = groups.map(g => {
					let { order } = g;
					if (order == oldIndex) order = newIndex;
					else if (order > oldIndex && order <= newIndex) order -= 1;
					else if (order < oldIndex && order >= newIndex) order += 1;
					return { id: g.id, order: order };
				});

				commitMutation<HomePanelMoveGroupMutationType>(environment, {
					mutation: moveGroupMutation,
					variables: { groupId: draggableId, position: destination.index },
					optimisticResponse: { moveGroup: { viewer: { id: viewer.id, homePanelGroups: optimisticGroups } } },
				});
			} else {
				throw new Error(`Unrecognized Home panel drag & drop type: ${type}`);
			}
		},
		[groups, viewer.id],
	);

	const [currentlyDragging, setCurrentlyDragging] = useState<HomePanelContextValue["currentlyDragging"]>(null);
	const panelContextValue: HomePanelContextValue = useMemo(
		() => ({
			viewer: viewer,
			currentlyDragging,
			teams: viewer.myTeams ?? [],
		}),
		[currentlyDragging, viewer],
	);

	const allDraggableItems = useMemo(() => {
		const itemGroups = groups.map(({ id, __typename }) => ({ id, __typename }));
		const items = groups
			.map(g =>
				g.items.edges
					?.map(edge => edge?.node)
					.filter(isNotNull)
					.filter(isNotOther)
					.map(obj =>
						// using a ternary here so TypeScript preserves the discriminated union type
						"type" in obj
							? { id: obj.id, __typename: obj.__typename, type: obj.type }
							: { id: obj.id, __typename: obj.__typename },
					),
			)
			.flat(1);
		return [...itemGroups, ...items];
	}, [groups]);
	const onDragStart = useCallback(
		(start: DragStart) => {
			let draggingItem = allDraggableItems.find(({ id }) => id === start.draggableId);
			if (!draggingItem && atob(start.draggableId).startsWith("Event:"))
				// TODO: The type annotation here is a bit confusing and needs figuring out: It looks like the underlying type
				// should allow `Event` as well (it's already allowing `Tag | User`).
				// @ts-ignore
				draggingItem = { id: start.draggableId, __typename: "Event" };
			setCurrentlyDragging(draggingItem ?? null);
		},
		[allDraggableItems],
	);

	const draggableInnerRenderer = useMemo(
		() => getDraggableInnerRenderer(groups, viewer, openInviteModalPlus),
		[groups, viewer, openInviteModalPlus],
	);

	const existingOneOnOneEmails = useMemo(() => {
		const emails = new Set<string>();
		for (const group of groups) {
			for (const edge of group.items.edges) {
				if (!edge?.node) continue;
				const { node } = edge;
				if ("otherUser" in node && node.otherUser) {
					emails.add(node.otherUser?.email);
				} else if ("email" in node) {
					emails.add(node.email);
				}
			}
		}
		return emails;
	}, [groups]);

	const filterExistingOneOnOnes = useCallback<FilterFunction>(
		({ node: contact }) => {
			return !existingOneOnOneEmails.has(contact.email);
		},
		[existingOneOnOneEmails],
	);

	const { hasDismissed, dismiss } = React.useContext(DismissibleElementsContext);
	const dismissGroupsTooltip = useCallback(() => {
		dismiss(HOME_PANEL_OVER_TEN_KEY);
	}, [dismiss]);

	const items = groups
		.filter(g => g.type != "MEETINGS")
		.map(g => g.items.edges.map(edge => edge?.node))
		.flat(1);

	const showGroupsTooltip = items.length >= 10 && !hasDismissed(HOME_PANEL_OVER_TEN_KEY);

	useEffect(() => {
		if (showGroupsTooltip) {
			dispatchAnalyticsTrack("Shown Home Panel Customization Tool Tip");
		}
	}, [showGroupsTooltip]);

	const supportsHover = useMedia("(hover: hover)");

	return (
		<>
			{!viewer.user.isGuest &&
				headerActionsContainerRef.current &&
				ReactDOM.createPortal(
					<div className={navPanelStyles.panelHeaderActions}>
						{viewer.account.allowInviting && (
							<IconButton
								title="Invite"
								icon={faUserPlus}
								onClick={openInviteModal}
								data-test-id="home-panel__invite"
							/>
						)}
						<Menu>
							<Tooltip
								placement="right-start"
								visible={showGroupsTooltip}
								interactive
								content={
									<DismissibleTooltip
										dismiss={dismissGroupsTooltip}
										icon={faStar}
										text="Create groups to organize your notes and tags in your home panel."
									/>
								}
							>
								<Menu.Trigger>
									<IconButton title="Options" icon={faEllipsisV} data-test-id="home-panel__kebab" />
								</Menu.Trigger>
							</Tooltip>
							<Menu.Content data-test-id="home-panel__menu">
								<CreateGroupButton />
							</Menu.Content>
						</Menu>
					</div>,
					headerActionsContainerRef.current,
				)}
			{objectiveIconRef.current &&
				ReactDOM.createPortal(
					<>
						{viewer.unreadBeacons.includes("Navbar__objectives") ? (
							<PrimaryNavLinkUnreadDot />
						) : (
							<FontAwesomeIcon icon={faBullseyeArrow} fixedWidth />
						)}
					</>,
					objectiveIconRef.current,
				)}

			<HomePanelContext.Provider value={panelContextValue}>
				<DragDropContext onDragStart={onDragStart} onDragEnd={onDropItem}>
					<Droppable type="GROUP" droppableId="ROOT" renderClone={draggableInnerRenderer}>
						{provided => (
							<div {...provided.droppableProps} ref={provided.innerRef}>
								{groups.map((group, index) => (
									<Draggable
										key={group.id}
										draggableId={group.id}
										index={index}
										isDragDisabled={!supportsHover}
										disableInteractiveElementBlocking // because our main drag handle area is the title, a button
									>
										{draggableInnerRenderer}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</Droppable>
				</DragDropContext>
			</HomePanelContext.Provider>

			{oneOnOneInviteOpen && (
				<OneOnOneInvite
					onClose={closeOneOnOneInvite}
					onComplete={onNewOneOnOneInvitees}
					filter={filterExistingOneOnOnes}
					// eslint-disable-next-line camelcase
					analytics={{ ux_location: inviteModalSource }}
				/>
			)}
		</>
	);
}

function HomePanel(): React.ReactElement {
	const headerActionsRef = React.useRef<HTMLDivElement>(null);
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const objectiveIconRef = useRef<HTMLDivElement>(null);
	const groupsContainerRef = useRef<HTMLDivElement>(null);
	const { user } = React.useContext(AppContext);
	const { lastQuery: lastSearchQuery } = useContext(GlobalSearchContext);
	const searchHref = useMemo<LocationDescriptor>(
		() => ({
			pathname: "/search/",
			search: lastSearchQuery,
		}),
		[lastSearchQuery],
	);

	const searchIsActive = useCallback<NonNullable<NavLinkProps["isActive"]>>((_match, location) => {
		return location.pathname.startsWith("/search/");
	}, []);

	return (
		<section className={cx(navPanelStyles.panel, panelStyles.panelBase)}>
			<header className={navPanelStyles.panelHeader}>
				<h1>Home</h1>
				<div ref={headerActionsRef}></div>
			</header>
			<div ref={scrollContainerRef} className={navPanelStyles.main}>
				<ScrollContext.Provider value={scrollContainerRef ? scrollContainerRef.current : null}>
					<div className={cx(panelSectionStyles.panelSection, panelSectionStyles.panelSectionPrimary)}>
						<div className={cx(panelSectionStyles.panelSectionContent)}>
							{user && !user.isGuest && (
								<PrimaryNavLink to={searchHref} isActive={searchIsActive}>
									<FontAwesomeIcon icon={faSearch} fixedWidth />
									Search
								</PrimaryNavLink>
							)}
							{!isNative && (
								<PrimaryNavLink to="/calendar/">
									<FontAwesomeIcon icon={faCalendarWeek} fixedWidth />
									My Week
								</PrimaryNavLink>
							)}
							<PrimaryNavLink to="/action-items/">
								<FontAwesomeIcon icon={faCheckSquare} fixedWidth />
								Action Items
							</PrimaryNavLink>
							<PrimaryNavLink to="/activity-feed/">
								<FontAwesomeIcon icon={faHeartRate} fixedWidth />
								Activity Feed
							</PrimaryNavLink>
						</div>
					</div>
					<div ref={groupsContainerRef}>
						<React.Suspense
							fallback={
								<>
									<div style={{ position: "relative", minHeight: "120px" }}>
										<LoadingSpinner inline />
									</div>
									<DragDropContext onDragStart={noop} onDragEnd={noop}>
										<MeetingsGroup
											isDragging={false}
											group={{
												id: "fake",
												type: "MEETINGS",
												name: "Meetings",
												items: { edges: [] },
												emojiName: "",
												" $fragmentType": "HomePanelGroup_group",
											}}
										/>
									</DragDropContext>
								</>
							}
						>
							<HomePanelDataRequiredRenderer
								objectiveIconRef={objectiveIconRef}
								headerActionsContainerRef={headerActionsRef}
							/>
						</React.Suspense>
					</div>
				</ScrollContext.Provider>
			</div>
		</section>
	);
}

export default React.memo(HomePanel);
